import React from 'react';
import { useNavigate } from 'react-router-dom';
import { SEOHead } from '../../components/seo/SEOHead';
import { FAQSchema, BreadcrumbSchema } from '../../components/seo/SchemaMarkup';
import { Breadcrumbs } from '../../components/seo/Breadcrumbs';
import { LatestArticles } from '../../components/blog/LatestArticles';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../../components/ui/accordion';
import { Badge } from '../../components/ui/badge';
import { PartyPopper, Sparkles, Music, Crown, Clock, ArrowRight } from 'lucide-react';

export default function NewYearPartyJamshedpur() {
  const navigate = useNavigate();

  const faqs = [
    {
      question: "When does the New Year's Eve party start at Scratch Unleashed?",
      answer: "Our New Year's Eve celebration begins at 9 PM on December 31st and continues past midnight until 5 AM on January 1st. We recommend arriving between 9-10 PM to settle in before the countdown excitement begins."
    },
    {
      question: "What is included in New Year's Eve entry tickets?",
      answer: "General entry tickets include access to the main venue, DJ performances, countdown celebration, complimentary champagne toast at midnight, and party favors (hats, blowers, glow accessories). VIP packages include reserved seating, premium bottle service, and enhanced experiences."
    },
    {
      question: "How much are tickets for the New Year party?",
      answer: "Ticket prices vary by tier. Early bird general entry starts from ₹3000 per person. Standard pricing is ₹4000-₹5000. VIP table packages range from ₹40,000 to ₹100,000+ depending on table location and bottle inclusions. Prices increase as we approach December 31st, so early booking is recommended."
    },
    {
      question: "Is there a dress code for New Year's Eve?",
      answer: "Yes, we enforce a strict formal/cocktail dress code for New Year's Eve. Gentlemen: dress shirts, blazers/suits, formal trousers, dress shoes (no sneakers). Ladies: cocktail dresses, evening wear, formal attire. This is our biggest night of the year—dress to impress!"
    },
    {
      question: "Can I book a table for New Year's Eve?",
      answer: "Absolutely! VIP table bookings are highly recommended for New Year's Eve as they provide the best experience and sell out quickly. Tables guarantee reserved seating, bottle service, priority access, and the best views for the midnight countdown. We recommend booking 2-3 months in advance."
    },
    {
      question: "What happens at midnight?",
      answer: "At midnight, we orchestrate a spectacular countdown experience with synchronized visuals, confetti cannons, balloon drops, champagne service, and a special DJ moment. The entire venue erupts in celebration as we ring in the new year together."
    }
  ];

  const breadcrumbItems = [
    { name: 'New Year Party in Jamshedpur', url: null }
  ];

  return (
    <div className="min-h-screen bg-[var(--bg)] text-[var(--text)]">
      <SEOHead
        title="New Year Party in Jamshedpur 2026 | NYE Celebration at Scratch Unleashed"
        description="Ring in 2026 at Scratch Unleashed - Jamshedpur's biggest New Year's Eve party! World-class DJs, VIP packages, champagne countdown, premium nightlife. Book now!"
        keywords="New Year party Jamshedpur, New Year's Eve Jamshedpur, NYE 2026 Jamshedpur, New Year celebration Jamshedpur, countdown party Jamshedpur, best New Year party Jamshedpur"
        canonical="https://scratchjsr.com/new-year-party-jamshedpur"
      />
      <FAQSchema faqs={faqs} />
      <BreadcrumbSchema items={[
        { name: 'Home', url: 'https://scratchjsr.com/' },
        { name: 'New Year Party in Jamshedpur', url: 'https://scratchjsr.com/new-year-party-jamshedpur' }
      ]} />

      <div className="h-16"></div>

      <div className="max-w-[1120px] mx-auto px-4 sm:px-6 py-12">
        <Breadcrumbs items={breadcrumbItems} />

        {/* Hero Section */}
        <div className="mb-12">
          <div className="flex items-center gap-3 mb-4">
            <Badge className="bg-[var(--accent-gold)] text-black text-xs px-3 py-1">LIMITED TICKETS</Badge>
            <Badge variant="outline" className="border-[var(--accent-crimson)] text-[var(--accent-crimson)] text-xs px-3 py-1">BOOK NOW</Badge>
          </div>
          <h1 className="font-bebas text-5xl sm:text-6xl lg:text-7xl mb-4">
            NEW YEAR PARTY IN JAMSHEDPUR 2026
          </h1>
          <p className="text-xl text-[var(--text-dim)] mb-6">
            The Ultimate New Year's Eve Celebration
          </p>
          <div className="flex flex-col sm:flex-row gap-3">
            <Button className="btn-crimson-glow rounded-xl" onClick={() => navigate('/checkin')}>
              Book Your Tickets
              <ArrowRight size={16} className="ml-2" />
            </Button>
            <Button variant="outline" className="rounded-xl border-[var(--border-custom)]" onClick={() => navigate('/vip-table-booking-jamshedpur')}>
              VIP Table Packages
            </Button>
          </div>
        </div>

        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
          <div className="lg:col-span-2 space-y-8">
            {/* Introduction */}
            <section>
              <h2 className="font-bebas text-3xl mb-4">Jamshedpur's Premier New Year's Eve Destination</h2>
              <div className="space-y-4 text-[var(--text-dim)] leading-relaxed">
                <p>
                  When it comes to celebrating <strong>New Year's Eve in Jamshedpur</strong>, Scratch Unleashed stands alone as the city's most anticipated annual celebration. Year after year, we've earned our reputation as the ultimate destination for ringing in the new year with style, energy, and unforgettable moments. Our New Year's Eve party isn't just another event—it's THE event that sets the standard for how to properly celebrate the transition into a new year.
                </p>
                <p>
                  What makes the <strong>New Year party at Scratch Unleashed</strong> so special? It's the perfect combination of world-class entertainment, premium production value, and an atmosphere of pure celebration. From the moment doors open at 9 PM until the final song plays in the early hours of January 1st, every element is designed to create the most memorable night of the year. This isn't just a party—it's a full-scale production featuring multiple DJs, live performances, spectacular visual effects, and a midnight countdown experience that rivals any celebration in the country.
                </p>
              </div>
            </section>

            {/* What to Expect */}
            <section>
              <h2 className="font-bebas text-3xl mb-4">The Complete NYE Experience</h2>
              <div className="space-y-4 text-[var(--text-dim)] leading-relaxed">
                <p>
                  Our <strong>New Year's Eve celebration in Jamshedpur</strong> is carefully orchestrated to build energy throughout the night, culminating in an explosive midnight moment. Here's what you can expect:
                </p>
                <p>
                  <strong>9 PM - 11 PM: Pre-Countdown Build</strong><br />
                  The evening begins with our resident DJs warming up the crowd with a perfect mix of feel-good anthems and dance floor favorites. This is the time to settle into your table or spot, order your first round, reconnect with friends, and let the anticipation build. The lighting design during these early hours sets a glamorous, upscale tone—think premium nightclub meets high-energy party. (Note: For New Year's Eve, we open earlier than our usual 6 PM weekend start to give everyone time to arrive and settle in.)
                </p>
                <p>
                  <strong>11 PM - 11:59 PM: The Final Hour</strong><br />
                  As we approach midnight, the energy shifts. Our headlining DJ takes over, delivering a powerhouse set designed to bring the entire venue to peak excitement. The music gets bigger, the lights get brighter, and the crowd's energy becomes electric. During this final hour, our staff distributes party favors—champagne flutes, glow accessories, confetti poppers, and New Year's hats. Everyone is on the dance floor, hands in the air, ready for the moment we've all been waiting for.
                </p>
                <p>
                  <strong>11:59 PM - 12:01 AM: The Countdown</strong><br />
                  This is the moment that defines the entire night. At 11:59, the music pauses. Our LED screens display a massive synchronized countdown. The entire venue—hundreds of voices—counts down together: "10... 9... 8..." At midnight, the venue explodes. Confetti cannons fire from the ceiling, balloons drop from above, champagne flows, and the DJ drops the biggest anthem of the night. Strangers hug, friends toast, and for a few magical moments, everyone is united in pure celebration. This is why people come to Scratch Unleashed for New Year's Eve.
                </p>
                <p>
                  <strong>12:01 AM - 5 AM: New Year Celebration</strong><br />
                  The party doesn't stop at midnight—it intensifies. With the new year officially begun, our DJs deliver their most powerful sets, mixing everything from house and hip-hop to Bollywood bangers and international hits. The dance floor stays packed for hours as everyone celebrates the fresh start and the incredible night we've shared together.
                </p>
              </div>
            </section>

            {/* Ticket Tiers */}
            <section>
              <h2 className="font-bebas text-3xl mb-4">Ticket Options & Packages</h2>
              <div className="space-y-6">
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <h3 className="font-semibold text-lg text-[var(--text)]">Early Bird General Entry</h3>
                    <Badge className="text-xs">₹3000</Badge>
                  </div>
                  <p className="text-[var(--text-dim)] mb-3">
                    Book early and save! Includes venue access, DJ performances, midnight countdown, complimentary champagne toast, and party favors. Limited quantity available. These tickets sell out first, so secure yours as soon as our New Year's Eve tickets go on sale (typically late October).
                  </p>
                </div>
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <h3 className="font-semibold text-lg text-[var(--text)]">Standard General Entry</h3>
                    <Badge className="text-xs">₹4000-₹5000</Badge>
                  </div>
                  <p className="text-[var(--text-dim)] mb-3">
                    Full access to the main venue with all the celebration elements. Price increases as we get closer to December 31st, with final tier pricing at ₹5000. Includes everything in early bird tier but at regular pricing.
                  </p>
                </div>
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <h3 className="font-semibold text-lg text-[var(--text)]">VIP Table Packages</h3>
                    <Badge className="text-xs bg-[var(--accent-gold)] text-black">₹40,000 - ₹100,000+</Badge>
                  </div>
                  <p className="text-[var(--text-dim)] mb-3">
                    The ultimate New Year's Eve experience. Reserved premium tables, priority entry, dedicated servers, bottle service packages, enhanced party favors, and the best views of the midnight countdown. Pricing varies based on table location, group size, and bottle selections. Stage-side tables and exclusive lounges command premium pricing but deliver an unforgettable experience. These book out months in advance.
                  </p>
                </div>
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <h3 className="font-semibold text-lg text-[var(--text)]">Couples Package</h3>
                    <Badge variant="outline" className="text-xs border-[var(--accent-gold)] text-[var(--accent-gold)]">Special</Badge>
                  </div>
                  <p className="text-[var(--text-dim)] mb-3">
                    Designed for couples wanting a romantic yet energetic New Year's celebration. Includes entry for two, reserved couple seating area, complimentary champagne bottle, premium party favors, and a commemorative photo. Limited availability at ₹10,000 per couple.
                  </p>
                </div>
              </div>
            </section>

            {/* Entertainment Lineup */}
            <section>
              <h2 className="font-bebas text-3xl mb-4">Entertainment & Production</h2>
              <div className="space-y-4 text-[var(--text-dim)] leading-relaxed">
                <p>
                  Every year, we curate a special entertainment lineup for our <strong>New Year party in Jamshedpur</strong> that goes beyond our already impressive regular offerings. For New Year's Eve 2026, we're bringing in:
                </p>
                <p>
                  <strong>Guest Headlining DJs:</strong> We typically feature 2-3 nationally recognized DJs who've performed at major festivals like Sunburn, Vh1 Supersonic, and international venues. These artists bring their A-game for New Year's Eve, delivering special extended sets designed specifically for the countdown energy. Artist announcements typically happen in November.
                </p>
                <p>
                  <strong>Enhanced Production:</strong> Our standard lighting and sound system gets a massive upgrade for NYE. We bring in additional LED panels, lasers, special effects equipment, confetti cannons, CO2 jets, and balloon drop systems. The visual experience is designed to match the musical energy at every moment.
                </p>
                <p>
                  <strong>Live Elements:</strong> In addition to our DJ lineup, we often incorporate live percussion, surprise performances, and interactive elements throughout the night to keep the energy fresh and exciting.
                </p>
              </div>
            </section>

            {/* Planning Tips */}
            <section>
              <h2 className="font-bebas text-3xl mb-4">Planning Your Perfect NYE Night</h2>
              <div className="space-y-4 text-[var(--text-dim)] leading-relaxed">
                <p>
                  To make the most of your <strong>New Year's Eve celebration at Scratch Unleashed</strong>, keep these tips in mind:
                </p>
                <p>
                  <strong>Book Early:</strong> Our New Year's Eve event sells out every single year. General entry tickets typically become available in late October or early November. VIP tables can be reserved even earlier. Don't wait until December—by then, your options will be extremely limited.
                </p>
                <p>
                  <strong>Dress to Impress:</strong> New Year's Eve is our most formal event of the year. Our dress code is strictly enforced, and our guests traditionally dress in their best cocktail attire and formal wear. This creates an incredibly sophisticated atmosphere that elevates the entire experience.
                </p>
                <p>
                  <strong>Arrive Early:</strong> While the countdown is at midnight, arriving around 9-10 PM gives you time to settle in, avoid peak entry rushes, and start your celebration at a comfortable pace. VIP table holders should coordinate arrival times with their server.
                </p>
                <p>
                  <strong>Plan Transportation:</strong> New Year's Eve is the busiest night of the year everywhere. Coordinate designated drivers, book cabs in advance, or arrange ride-sharing well before midnight. Responsible planning ensures everyone gets home safely.
                </p>
              </div>
            </section>

            {/* Call to Action */}
            <section className="bg-[var(--surface)] border border-[var(--border-custom)] rounded-2xl p-8">
              <h2 className="font-bebas text-3xl mb-4">Don't Miss Jamshedpur's Biggest Night</h2>
              <p className="text-[var(--text-dim)] mb-6">
                New Year's Eve at Scratch Unleashed is more than a party—it's a tradition, a celebration, and a memory you'll carry into the new year. Tickets are limited and sell out every year. Secure your spot now and ring in 2026 with the biggest, boldest, most unforgettable celebration in Jamshedpur.
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <Button className="btn-crimson-glow rounded-xl" onClick={() => navigate('/checkin')}>
                  Book Tickets Now
                </Button>
                <Button variant="outline" className="rounded-xl border-[var(--border-custom)]" onClick={() => navigate('/vip-table-booking-jamshedpur')}>
                  Reserve VIP Table
                </Button>
              </div>
            </section>

            {/* FAQ Section */}
            <section>
              <h2 className="font-bebas text-3xl mb-6">Frequently Asked Questions</h2>
              <Accordion type="single" collapsible className="space-y-3">
                {faqs.map((faq, index) => (
                  <AccordionItem key={index} value={`item-${index}`} className="border border-[var(--border-custom)] rounded-xl px-4 bg-[var(--surface)]">
                    <AccordionTrigger className="hover:no-underline text-left">
                      <span className="font-semibold">{faq.question}</span>
                    </AccordionTrigger>
                    <AccordionContent className="text-sm text-[var(--text-dim)] pb-4">
                      {faq.answer}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </section>
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1 space-y-6">
            {/* Event Details */}
            <Card className="p-6 bg-[var(--surface)] border border-[var(--border-custom)] rounded-xl">
              <h3 className="font-bebas text-2xl mb-4">Event Details</h3>
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <Clock size={20} className="text-[var(--accent-gold)] mt-1" />
                  <div>
                    <p className="font-semibold text-sm">Timing</p>
                    <p className="text-sm text-[var(--text-dim)]">Dec 31, 9 PM - Jan 1, 5 AM</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <PartyPopper size={20} className="text-[var(--accent-gold)] mt-1" />
                  <div>
                    <p className="font-semibold text-sm">Highlights</p>
                    <p className="text-sm text-[var(--text-dim)]">Countdown, confetti, champagne</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Music size={20} className="text-[var(--accent-gold)] mt-1" />
                  <div>
                    <p className="font-semibold text-sm">Entertainment</p>
                    <p className="text-sm text-[var(--text-dim)]">Guest DJs + live elements</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Crown size={20} className="text-[var(--accent-gold)] mt-1" />
                  <div>
                    <p className="font-semibold text-sm">Dress Code</p>
                    <p className="text-sm text-[var(--text-dim)]">Formal/Cocktail attire required</p>
                  </div>
                </div>
              </div>
            </Card>

            {/* Ticket Pricing Summary */}
            <Card className="p-6 bg-[var(--surface)] border border-[var(--border-custom)] rounded-xl">
              <h3 className="font-bebas text-2xl mb-4">Ticket Pricing</h3>
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-sm">Early Bird</span>
                  <span className="font-semibold text-[var(--accent-gold)]">₹3000</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm">Standard Entry</span>
                  <span className="font-semibold text-[var(--accent-gold)]">₹4000-5000</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm">Couples Package</span>
                  <span className="font-semibold text-[var(--accent-gold)]">₹10,000</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm">VIP Tables</span>
                  <span className="font-semibold text-[var(--accent-gold)]">₹40,000+</span>
                </div>
                <p className="text-xs text-[var(--text-muted)] pt-2 border-t border-[var(--border-custom)]">
                  Limited availability. Book early for best prices.
                </p>
              </div>
            </Card>


            {/* Latest Articles */}
            <LatestArticles maxPosts={3} />

            {/* Related Pages */}
            <Card className="p-6 bg-[var(--surface)] border border-[var(--border-custom)] rounded-xl">
              <h3 className="font-bebas text-2xl mb-4">Explore More</h3>
              <div className="space-y-3">
                <button
                  onClick={() => navigate('/night-club-in-jamshedpur')}
                  className="w-full text-left text-sm text-[var(--accent-gold)] hover:underline"
                >
                  → Night Club in Jamshedpur
                </button>
                <button
                  onClick={() => navigate('/dj-night-jamshedpur')}
                  className="w-full text-left text-sm text-[var(--accent-gold)] hover:underline"
                >
                  → DJ Nights
                </button>
                <button
                  onClick={() => navigate('/vip-table-booking-jamshedpur')}
                  className="w-full text-left text-sm text-[var(--accent-gold)] hover:underline"
                >
                  → VIP Table Booking
                </button>
              </div>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}
